import type { RequestFn } from '../http';

/**
 * Was das endgültige Löschen sperrt (backend services/ticketPurgeBlockers.ts): gebuchte Zeiten,
 * Leistungsjournal, Abrechnungstopf, Berichte (auch Entwürfe), Wareneingänge, Kundenberichte,
 * Lager-Auslieferungen … — beide Mengen (Zeiten, Belege) gleich wie im Backend halten.
 */
export interface TicketPurgeBlocker {
  kind: string;
  label: string;
  count: number;
}

/** Fehlercode, mit dem der Server jeden Lösch-Schritt ablehnt, solange Abrechnungsdaten am Ticket hängen. */
export const TICKET_HAS_BILLING_DATA = 'TICKET_HAS_BILLING_DATA';

const TIME_BLOCKERS = new Set(['TIME_ENTRIES', 'TIME_LEDGER', 'TIME_BOOKINGS', 'PORTAL_TIME_BOOKINGS']);
const REPORT_BLOCKERS = new Set(['SUBMISSIONS', 'GOODS_RECEIPTS', 'CUSTOMER_REPORTS', 'STOCK_RETURNS', 'DELIVERY_ORDERS']);

/** Kurzform für Badge und Zeile — gleicher Satz wie `billingBlockedMessage` im Backend (ohne Anzahlen). */
export function purgeBlockedText(blockers: TicketPurgeBlocker[]): string {
  const kinds = blockers.map((b) => b.kind);
  const parts: string[] = [];
  if (kinds.some((k) => TIME_BLOCKERS.has(k))) parts.push('gebuchte Zeiten');
  if (kinds.some((k) => REPORT_BLOCKERS.has(k))) parts.push('Berichte');
  if (parts.length === 0 || kinds.some((k) => !TIME_BLOCKERS.has(k) && !REPORT_BLOCKERS.has(k))) parts.push('Abrechnungsdaten');
  return `Dieses Ticket hat ${parts.join(' / ')} — endgültig löschen ist nicht möglich. Es bleibt im Papierkorb.`;
}

/** null = liegt im Papierkorb, 'PURGED' = in Weclapp gelöscht, 'FAILED' = Weclapp hat abgelehnt. */
export type TicketPurgeState = 'PURGED' | 'FAILED' | null;

export interface TrashedTicket {
  id: string;
  ticketNumber: string | null;
  subject: string | null;
  status: string | null;
  partyName: string | null;
  contactName: string | null;
  createdDate: string | null;
  trashedAt: string | null;
  trashedByName: string | null;
  /** Tage, bis die endgültige Löschung beantragt werden darf (0 = Frist abgelaufen). Nie automatisch. */
  daysUntilPermanentDeletion: number;
  /** Ab wann die endgültige Löschung beantragt werden darf (10 Jahre nach dem Papierkorb). */
  purgeEligibleAt?: string | null;
  purgeState: TicketPurgeState;
  purgeError: string | null;
  purgedAt: string | null;
  /** Leer = endgültig löschbar (was Abrechnungsdaten angeht); fehlt = nicht geprüft. */
  purgeBlockers?: TicketPurgeBlocker[];
}

export interface TicketTrashListResponse {
  tickets: TrashedTicket[];
  totalCount: number;
  page: number;
  pageSize: number;
  retentionDays: number;
  /** Angemeldeter Admin ist Super-Admin (nur Anzeige — der Server prüft bei jedem Schritt erneut). */
  superAdmin?: boolean;
}

/** Bestätigungstext der Super-Admin-Einzellöschung — muss zu SUPER_PURGE_PHRASE im Backend passen. */
export const SUPER_PURGE_PHRASE = 'ENDGÜLTIG LÖSCHEN';
/** Mindestlänge der Begründung — muss zu SUPER_PURGE_REASON_MIN im Backend passen. */
export const SUPER_PURGE_REASON_MIN = 20;

/** Grabstein einer endgültigen Löschung (backend services/ticketDeletionRecords.ts). */
export interface TicketDeletionRecord {
  id: string;
  ticketId: string;
  ticketNumber: string | null;
  weclappId: string | null;
  subject: string | null;
  customerName: string | null;
  customerNumber: string | null;
  trashedAt: string | null;
  trashedByName: string | null;
  method: 'SUPER_ADMIN' | 'FOUR_EYES';
  deletedByName: string | null;
  approvedByName: string | null;
  reason: string;
  approvalNote: string | null;
  purgeRequestId: string | null;
  status: 'PENDING' | 'DELETED' | 'FAILED';
  error: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface TicketDeletionRecordList {
  records: TicketDeletionRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/**
 * Ab dieser Menge verlangt das Backend eine ausdrückliche Freigabe: Vorschau der
 * betroffenen Tickets plus Eintippen von TICKET_TRASH_CONFIRM_PHRASE.
 * Muss zu BULK_CONFIRM_THRESHOLD in routes/ticketTrash.ts passen.
 */
export const TICKET_TRASH_CONFIRM_THRESHOLD = 10;
export const TICKET_TRASH_CONFIRM_PHRASE = 'LÖSCHEN';
/** Höchstens so viele Tickets je Löschvorgang — muss zu TRASH_BULK_LIMIT in services/ticketTrash.ts passen. */
export const TICKET_TRASH_BULK_LIMIT = 2000;

/** Schlanke Ticket-Zeile für die Vorschau vor einer Massenlöschung. */
export interface TicketPreviewRow {
  id: string;
  ticketNumber: string | null;
  subject: string | null;
  partyName: string | null;
  status: string | null;
  createdDate: string | null;
}

/** Filter der Ticketliste — muss zum Backend-Schema in routes/ticketTrash.ts passen. */
export interface TicketTrashFilter {
  search?: string;
  statusIds?: string[];
  priorityId?: string;
  assignment?: 'all' | 'assigned' | 'unassigned';
  customerMissing?: boolean;
  openOnly?: boolean;
  closedOnly?: boolean;
  /** ISO-Zeitfenster — Tab „Werktag" schränkt die Liste auf createdDate ein. */
  createdDateFrom?: string;
  createdDateTo?: string;
}

export interface TicketTrashByFilterBody {
  search?: string;
  statusIds?: string[];
  priorityId?: string;
  assignment?: 'all' | 'assigned' | 'unassigned';
  customerMissing?: boolean;
  openOnly?: boolean;
  closedOnly?: boolean;
  createdDateFrom?: string;
  createdDateTo?: string;
  /** Trefferzahl aus der Anzeige — das Backend lehnt mit 409 ab, wenn sie nicht mehr stimmt. */
  confirmCount: number;
  /** Ab TICKET_TRASH_CONFIRM_THRESHOLD Pflicht: der eingetippte Bestätigungstext. */
  confirmPhrase?: string;
}

export interface TicketTrashMutationResult {
  success: boolean;
  trashed?: number;
  restored?: number;
  skipped?: number;
  purged?: number;
  failed?: number;
  errors?: Array<{ ticketId: string; ticketNumber: string | null; message: string }>;
  retentionDays?: number;
  message: string;
}

/** Antrag auf endgültiges Löschen — ein zweiter Admin bestätigt (routes/ticketTrash.ts). */
export interface TicketPurgeRequest {
  id: string;
  ticketId: string;
  ticketNumber: string | null;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'EXECUTED' | 'FAILED';
  requestedByUserId: string;
  requestedByName: string | null;
  requestedAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  executedAt: string | null;
  error: string | null;
}

export interface TicketPurgeRequestList {
  requests: TicketPurgeRequest[];
  retentionDays: number;
  /** ID des angemeldeten Admins — eigene Anträge kann er nicht bestätigen. */
  me: string;
}

export function createTicketTrashApi(request: RequestFn) {
  return {
    /** Ausgewählte Tickets in den Papierkorb verschieben (Admin). */
    async trashTickets(ids: string[], confirmPhrase?: string) {
      return request<TicketTrashMutationResult>('/tickets/trash', {
        method: 'POST',
        body: JSON.stringify({ ids, confirmPhrase }),
      });
    },

    /**
     * Zeigt vorab, welche Tickets betroffen wären (Freigabe-Vorschau).
     * `ids` hat Vorrang vor dem Filter — nötig, weil eine Auswahl seitenübergreifend
     * bestehen bleibt und das Frontend nur die sichtbare Seite kennt.
     */
    async previewTicketTrash(input: TicketTrashFilter & { ids?: string[] }) {
      return request<{
        tickets: TicketPreviewRow[];
        totalCount: number;
        confirmThreshold: number;
      }>('/tickets/trash/preview', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },

    /** Alle Treffer des aktuellen Listen-Filters in den Papierkorb verschieben (Admin). */
    async trashTicketsByFilter(body: TicketTrashByFilterBody) {
      return request<TicketTrashMutationResult>('/tickets/trash/by-filter', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async getTicketTrash(
      params: { search?: string; ticketId?: string; page?: number; pageSize?: number } = {},
    ) {
      const q = new URLSearchParams();
      if (params.search) q.set('search', params.search);
      if (params.ticketId) q.set('ticketId', params.ticketId);
      if (params.page) q.set('page', String(params.page));
      if (params.pageSize) q.set('pageSize', String(params.pageSize));
      const query = q.toString() ? `?${q.toString()}` : '';
      return request<TicketTrashListResponse>(`/tickets/trash${query}`);
    },

    async getTicketTrashCount() {
      return request<{ count: number }>('/tickets/trash/count');
    },

    async restoreTrashedTickets(ids: string[]) {
      return request<TicketTrashMutationResult>('/tickets/trash/restore', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      });
    },

    /** Offene Löschanträge (Vier-Augen-Prinzip). */
    async getTicketPurgeRequests() {
      return request<TicketPurgeRequestList>('/tickets/trash/purge-requests');
    },

    /** Endgültiges Löschen beantragen — erst nach Ablauf der Frist, ein anderer Admin bestätigt. */
    async requestTicketPurge(ids: string[], reason: string) {
      return request<{ requested: number; skipped: number; message: string; results: Array<{ ticketId: string; created: boolean; error?: string }> }>(
        '/tickets/trash/purge-requests',
        { method: 'POST', body: JSON.stringify({ ids, reason }) },
      );
    },

    async approveTicketPurge(id: string, note?: string) {
      return request<{ status: string; detail?: string }>(`/tickets/trash/purge-requests/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
    },

    async rejectTicketPurge(id: string, note: string) {
      return request<{ status: string }>(`/tickets/trash/purge-requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
    },

    async cancelTicketPurge(id: string) {
      return request<{ status: string }>(`/tickets/trash/purge-requests/${id}/cancel`, { method: 'POST', body: '{}' });
    },

    /** Super-Admin, Schritt 1: Begründung → Einmal-Bestätigung (5 Minuten, nur dieses Ticket). */
    async authorizeSuperPurge(ticketId: string, reason: string) {
      return request<{ token: string; expiresAt: string; ticketNumber: string | null }>(
        `/tickets/trash/${encodeURIComponent(ticketId)}/super-purge/authorize`,
        { method: 'POST', body: JSON.stringify({ reason }) },
      );
    },

    /** Super-Admin, Schritt 2: Ticketnummer abtippen + Bestätigungstext → endgültig gelöscht. */
    async executeSuperPurge(ticketId: string, token: string, ticketNumber: string, phrase: string) {
      return request<{ status: string; recordId: string; message: string; contentPurged?: boolean }>(
        `/tickets/trash/${encodeURIComponent(ticketId)}/super-purge/execute`,
        { method: 'POST', body: JSON.stringify({ token, ticketNumber, phrase }) },
      );
    },

    /** Grabsteine endgültig gelöschter Tickets (Admin). */
    async getTicketDeletionRecords(
      params: { search?: string; method?: 'SUPER_ADMIN' | 'FOUR_EYES'; status?: 'PENDING' | 'DELETED' | 'FAILED'; page?: number; pageSize?: number } = {},
    ) {
      const q = new URLSearchParams();
      if (params.search) q.set('search', params.search);
      if (params.method) q.set('method', params.method);
      if (params.status) q.set('status', params.status);
      if (params.page) q.set('page', String(params.page));
      if (params.pageSize) q.set('pageSize', String(params.pageSize));
      const query = q.toString() ? `?${q.toString()}` : '';
      return request<TicketDeletionRecordList>(`/tickets/deletion-records${query}`);
    },
  };
}
